/**
 * SafeCurves AppConfig — composed from the per-slot modules in this directory.
 */

import type { AppConfig } from '#lib/apps/types.js';
import { LngLatBounds } from 'maplibre-gl';
import {
	EInterventions,
	EPrioritize,
	ESafeCurvesModule,
	type ISafeCurvesSystemLayers
} from './interfaces';
import LogoSafeCurves from '#lib/assets/logos/safecurves.svg?raw';

import { localSourceId, modules, SC_DATASET } from './layers';
import { buildFilters } from './filters';
import SummaryDataContent from './widgets/SummaryDataContent.svelte';
import { dashboard, programs, method, playbook } from './header';
import { loadSystem, loadSummary } from './loaders';
import { parseLegendLayers } from './legendHelpers';
import { tableTabs, moduleTable } from './layerConfig';
import { isAtLeastVersion, type VersionLine } from './versionSupport';
import { versionsStore } from '#lib/stores/versionsStore.svelte.js';
import { loadSafeCurvesSprite } from './loadMapSprites';
import { setLayerVisibility, parseFilterFromLegend } from '#lib/components/map/layers.js';
import stylePrioritize from './layer_styles/safecurves_prioritize_style.json';
import AttributesPanel from './AttributesPanel/AttributesPanel.svelte';
import ChatPanel from './ChatPanel/ChatPanel.svelte';
import ChatToggle from './ChatPanel/ChatToggle.svelte';
import ProgramsMapData from './programs/ProgramsMapData.svelte';
import { programsMapStore } from './programs/programsMapStore.svelte';
import Countermeasures from './Countermeasures/Countermeasures.svelte';
import IdentifyLegend from '../legends/IdentifyLegend.svelte';
import PrioritizeLegend from './legends/PrioritizeLegend.svelte';
import InterventionsLegend from './legends/InterventionsLegend.svelte';
import {
	ECrashes,
	ECrashesModule,
	type ICrashesSystemLayers
} from '#lib/apps/safesystem/context/interfaces.js';
import { sectionsFromModuleTable } from '#lib/components/map/legend/sectionsFromModuleTable.js';
import type { LegendSection } from '#lib/components/map/legend/types.js';
import { unitSystemStore } from '#lib/stores/unitSystemStore.svelte.js';
import { ableyUrl } from '#lib/stores/jurisdictionStore.svelte.js';

type SafeCurvesSystem = ISafeCurvesSystemLayers & ICrashesSystemLayers;

// Crashes overlay is a common layer outside the module table; append it.
const legendSections: LegendSection[] = [
	...sectionsFromModuleTable(moduleTable),
	{
		label: 'Crashes',
		layers: [
			{
				moduleId: ECrashesModule.Crashes,
				layerId: ECrashes.CrashesCircle,
				label: 'Fatal crash'
			}
		]
	},
	{
		label: 'Uploaded crash data',
		layers: [
			{
				moduleId: ECrashesModule.Crashes,
				layerId: ECrashes.UploadedCrashes,
				label: 'Uploaded crash'
			}
		]
	}
];

const localTabIds = new Set(
	Object.values(moduleTable).flatMap((m) =>
		Object.values(m.layers).flatMap((l) => (l.local && l.table ? [l.table.tabId] : []))
	)
);

// Columns tagged `meta.minVersion` show only from that version line; the getter stays live across version switches.
// Programs tabs are hidden (`optional` with no layer) until the active version has a project.
const versionedTableTabs = tableTabs.map((tab) => ({
	...tab,
	...(localTabIds.has(tab.id) && {
		optional: true,
		getLayerId: (systemData: SafeCurvesSystem) =>
			programsMapStore.hasProjects ? tab.getLayerId(systemData) : undefined
	}),
	get columns() {
		const version = versionsStore.version(SC_DATASET)?.version;
		return tab.columns.filter((c) => {
			const min = (c.meta as { minVersion?: VersionLine } | undefined)?.minVersion;
			return !min || isAtLeastVersion(version, min);
		});
	}
}));

export const safecurvesConfig: AppConfig<SafeCurvesSystem> = {
	id: 'safecurves',
	name: 'SafeCurves Professional',
	basePath: '/safecurves',
	head: {
		title: 'Abley SafeCurves Professional',
		description: 'Proactive, data-driven tools to identify road safety risks.',
		keywords: 'SafeSystem, SafeCurves, Abley Ltd.'
	},
	logo: LogoSafeCurves,
	basemap: 'no-roads',
	version: '1.2.4',
	get releaseNotesLink() {
		return ableyUrl('safecurves-updates');
	},
	// The crashes overlay is served alongside SafeCurves; it doesn't gate availability.
	datasets: [SC_DATASET],

	loaders: {
		system: loadSystem,
		summary: loadSummary
	},

	modules,
	legends: [
		{
			moduleId: ESafeCurvesModule.Identify,
			component: IdentifyLegend,
			// Pawnee PROD crash data predates per-layer year detail, hence the fallback.
			props: {
				heading: 'Curves',
				crashesFallback: '2015-2024',
				uploadNeedsCrashes: true
			}
		},
		{
			moduleId: ESafeCurvesModule.Prioritize,
			component: PrioritizeLegend
		},
		{ moduleId: ESafeCurvesModule.Interventions, component: InterventionsLegend }
	],
	legendSections,

	parseLegendLayers,

	map: {
		extraControls: [ChatToggle, ProgramsMapData],
		onLoad: loadSafeCurvesSprite,
		// Simplified low-zoom corridors mirror the Prioritize row's toggle and priority filter.
		onLayersSynced: (map, layers) => {
			const displayOnly = layers.find((l) => l.systemId === EPrioritize.PrioritizeDisplayOnly);
			const prioritize = layers.find((l) => l.systemId === EPrioritize.Prioritize);
			if (displayOnly && map.getLayer(displayOnly.layerId)) {
				setLayerVisibility(map, displayOnly.layerId, prioritize?.layerAnyEnabled ?? true);
				if (prioritize) map.setFilter(displayOnly.layerId, parseFilterFromLegend(prioritize));
			}
		},
		// Below the detailed layer's minzoom only the non-filterable simplified corridors show.
		dataFilterMinZoom: stylePrioritize.minzoom,
		// They're non-selectable, so suppress the "no map features" popover while only they show.
		hasNonQueryableData: (zoom, layers) => {
			if (zoom >= stylePrioritize.minzoom) return false;
			const prioritize = layers.find((l) => l.systemId === EPrioritize.Prioritize);
			return prioritize?.layerAnyEnabled ?? false;
		},
		// Getter so the map controls follow the jurisdiction once the metadata row loads.
		get measureUnits() {
			return unitSystemStore.systemFor('length');
		},
		// Home extent = combined bounds of every non-null SafeCurves layer (any single
		// module can be absent now). Crashes are excluded — a national
		// overlay that would blow the extent out well past the SafeCurves data.
		getDataBounds: (systemData) => {
			const scModules = [
				ESafeCurvesModule.Identify,
				ESafeCurvesModule.Prioritize,
				ESafeCurvesModule.Interventions
			];
			const bounds = new LngLatBounds();
			for (const moduleId of scModules) {
				const moduleLayers = systemData?.[moduleId];
				if (!moduleLayers) continue;
				for (const layer of Object.values(moduleLayers)) {
					const bbox = layer?.bbox;
					if (!bbox) continue;
					// filter out ~empty (apart from floating point noise) bboxes
					// test is degrees — well above float noise, well below any real bbox
					if (bbox.maxx - bbox.minx < 1e-5 && bbox.maxy - bbox.miny < 1e-5) continue;
					bounds.extend([
						[bbox.minx, bbox.miny],
						[bbox.maxx, bbox.maxy]
					]);
				}
			}
			return bounds.isEmpty()
				? undefined
				: (bounds.toArray().flat() as [number, number, number, number]);
		},
		getLabelsAnchorLayerId: (systemData) =>
			systemData?.[ECrashesModule.Crashes]?.[ECrashes.CrashesCircle]?.layer?.id,
		customCrashesSourceId: `${ECrashes.UploadedCrashes}-source`,
		geojsonSources: () => ({
			[localSourceId(EPrioritize.ProgramProjects)]: programsMapStore.features.projects,
			[localSourceId(EInterventions.ExistingSigns)]: programsMapStore.features.signs,
			[localSourceId(EInterventions.ExistingBarriers)]: programsMapStore.features.barriers
		})
	},

	table: {
		tabs: versionedTableTabs,
		defaultTabId: 'Corridors',
		filterPlaceholder: 'Filter table by road/county',
		// The Programs tabs push the row past a normal screen width.
		tabPlacement: 'left'
	},

	get filters() {
		return buildFilters();
	},

	dashboard: {
		widgets: [{ id: 'summary', component: SummaryDataContent }],
		backgroundImage: '/Curved_road_aerial.avif'
	},

	header: {
		dashboard,
		programs,
		method,
		playbook
	},

	attributes: {
		panel: AttributesPanel,
		countermeasures: Countermeasures,
		issueReporting: true
	},

	dataChat: ChatPanel
};
